import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, kindIcon } from "@/components/StatusBadge";
import { UploadCloud, Loader2, CheckCircle2, FolderInput, XCircle } from "lucide-react";

const STAGES = ["uploaded", "secured", "extracted", "analyzed", "categorized", "connected", "ready"];
const STAGE_LABELS = {
  uploaded: "Uploaded", secured: "Secured", extracted: "Text extracted", analyzed: "Analyzed",
  categorized: "Categorized", connected: "Connected", ready: "Ready", error: "Error",
};

function StageBar({ stage, progress, status }) {
  if (stage === "error") return <div className="text-xs text-red-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Processing error</div>;
  const idx = STAGES.indexOf(stage);
  return (
    <div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${progress || 5}%` }} />
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-slate-500">
        {STAGES.map((s, i) => (
          <span key={s} className={i <= idx ? "text-blue-400" : ""}>{i <= idx ? "●" : "○"}</span>
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-1">{STAGE_LABELS[stage] || stage} {status === "processing" ? "…" : ""}</div>
    </div>
  );
}

export default function Upload() {
  const [items, setItems] = useState([]); // tracked file ids
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const folderRef = useRef();
  const pollRef = useRef();

  const doUpload = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [...data.created, ...prev]);
      toast.success(`${data.created.length} file(s) uploaded. AI processing in background…`);
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  }, []);

  // Poll processing status
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      const active = items.filter((i) => i.status === "processing");
      if (!active.length) return;
      const updated = await Promise.all(
        items.map(async (i) => {
          if (i.status !== "processing") return i;
          try {
            const { data } = await api.get(`/uploads/${i.id}`);
            return { ...i, status: data.status, processing_stage: data.processing_stage, progress: data.progress, title: data.title, kind: data.kind, error: data.error };
          } catch { return i; }
        })
      );
      setItems(updated);
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [items]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) doUpload(e.dataTransfer.files);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-white tracking-tight">Upload Center</h1>
        <p className="text-slate-400 mt-2">Drop files, folders or archives. Every upload is preserved unchanged, hashed (SHA-256), then read and organized by AI.</p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid="upload-dropzone"
        className={`rounded-2xl border-2 border-dashed p-14 text-center transition-colors ${dragging ? "border-blue-500 bg-blue-500/5" : "border-white/10 bg-[#111827]/50"}`}
      >
        <div className={`w-16 h-16 rounded-2xl bg-blue-600/15 flex items-center justify-center mx-auto mb-5 ${dragging ? "animate-pulse" : ""}`}>
          <UploadCloud className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
        </div>
        <p className="text-white font-medium text-lg">Drag & drop files here</p>
        <p className="text-slate-500 text-sm mt-1">PDFs, images, Office docs, spreadsheets, media, text, ZIP — bulk uploads supported</p>
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => inputRef.current.click()} data-testid="browse-files-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Select files
          </button>
          <button onClick={() => folderRef.current.click()} data-testid="browse-folder-btn" className="inline-flex items-center gap-2 border border-white/10 hover:bg-white/5 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
            <FolderInput className="w-4 h-4" /> Select folder
          </button>
        </div>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => doUpload(e.target.files)} data-testid="file-input" />
        <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" hidden onChange={(e) => doUpload(e.target.files)} data-testid="folder-input" />
      </div>

      {items.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold text-white mb-4">Processing Queue</h2>
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.id} data-testid={`queue-item-${i.id}`} className="rounded-xl border border-white/5 bg-[#111827] p-4 grid md:grid-cols-[1fr_260px_auto] gap-4 items-center">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="text-blue-400 flex-shrink-0">{kindIcon(i.kind, "w-5 h-5")}</div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate font-medium">{i.title || i.original_filename}</div>
                    <div className="text-xs text-slate-500 truncate">{i.original_filename}</div>
                  </div>
                </div>
                <StageBar stage={i.processing_stage} progress={i.progress} status={i.status} />
                <div className="flex items-center gap-3 justify-end">
                  {i.status === "processing" ? (
                    <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  )}
                  <StatusBadge status={i.status} />
                  {i.status !== "processing" && <Link to={`/file/${i.id}`} className="text-xs text-blue-400 hover:text-blue-300" data-testid={`queue-open-${i.id}`}>Open</Link>}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-600 mt-4">You can navigate away — processing continues in the background. Review and publish items from the Review & Publish screen.</p>
        </section>
      )}
    </div>
  );
}
